import { useState, useCallback, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Calendar, DocumentText1, ArrowDown2, ArrowUp2, Edit2, Trash } from "iconsax-react";
import { Avatar, Badge, Button, Prompt, PromptForm } from "@/shared/ui";
import CheckIcon from "@/shared/assets/icons/CheckIcon";
import {
  SigningStatus,
  StatusIndicator,
  AMENDMENT_STATUS_MAPPING,
  AMENDMENT_DEFAULT,
  SIGNING_STATUS_INDICATOR_MAPPING,
  JOB_APPLICATION_DEFAULT,
  LEGACY_UPLOADED_DEFAULT,
} from "@/shared/components";
import { RESIGNATION_STATUS_MAPPING } from "@/features/hr/resignation-letters/consts/statusMappings";
import { formatDateForDisplay, type Locale } from "@/shared/utils";
import { AmendmentDetailsModal } from "@/features/hr/amendments";
import { ResignationLetterDetailsModal } from "@/features/hr/resignation-letters";
import type {
  CombinedContractListItem,
  ContractFillDraftListItem,
  ResignationLetterNested,
  AmendmentNested,
} from "@/features/hr/contracts/types";
import { SIGNING_STATUS_FULLY_SIGNED } from "@/features/hr/contracts/consts";
import { getAmendmentText, extractErrorMessage } from "@/features/hr/contracts/utils";
import { useDeleteContractFillDraftMutation } from "@/features/hr/contracts/api/contracts";
import { useContractsActions, useApplicationItems } from "@/features/hr/contracts/hooks";
import type { ApplicationItem } from "@/features/hr/contracts/hooks";
import ContractActionsButtons from "./ContractActionsButtons";
import JobApplicationDetailsModal from "./JobApplicationDetailsModal";
import OrderDetailsModal from "./OrderDetailsModal";

const PdfViewer = lazy(() => import("@/shared/ui/PdfViewer"));

interface GroupedContract {
  contract: CombinedContractListItem;
  resignationLetters: ResignationLetterNested[];
  amendments: AmendmentNested[];
}

interface Props {
  contracts: GroupedContract[];
  fillDrafts?: ContractFillDraftListItem[];
  locale: Locale;
  refetchContracts?: () => void;
}

type SelectedModal =
  | { type: "resignation_letter"; resignationId: number }
  | { type: "amendment"; amendmentId: number }
  | { type: "job_application"; candidateId: number; candidateName: string; candidateStage: string | null }
  | { type: "order"; orderId: number; candidateName: string; originalFileUrl: string | null }
  | null;

interface PdfViewerData {
  fileUrl: string;
  title: string;
  secondaryFileUrl?: string;
}

function insertNumberAfterFirstWord(text: string, number: number): string {
  const words = text.split(" ");
  if (words.length > 1) {
    return `${words[0]} №${number} ${words.slice(1).join(" ")}`;
  }
  return `${text} №${number}`;
}

export default function ContractsCardsView({ contracts, fillDrafts, locale }: Props) {
  const { t } = useTranslation("ContractsPage");
  const { t: tCommon } = useTranslation("Common");
  const navigate = useNavigate();
  const {
    isLoading,
    prompt,
    setPrompt,
    handleAction,
    downloadingContractId,
    previewingContractId,
    submittingContractId,
    previewingJobApplicationCandidateId,
    reviewingJobApplicationCandidateId,
    previewingOrderCandidateId,
    completingHiringCandidateId,
    documentSigning,
  } = useContractsActions();

  const [selectedModal, setSelectedModal] = useState<SelectedModal>(null);
  const [pdfViewerData, setPdfViewerData] = useState<PdfViewerData | null>(null);
  const [expandedApplications, setExpandedApplications] = useState<Set<number>>(new Set());
  const [confirmDeleteDraftId, setConfirmDeleteDraftId] = useState<number | null>(null);
  const [deletingDraftId, setDeletingDraftId] = useState<number | null>(null);

  const [deleteContractFillDraft] = useDeleteContractFillDraftMutation();

  const applicationItemsMap = useApplicationItems({ contracts });

  async function handleDeleteDraft(applicationId: number) {
    if (deletingDraftId !== null) return;
    try {
      setDeletingDraftId(applicationId);
      await deleteContractFillDraft(applicationId).unwrap();
      setConfirmDeleteDraftId(null);
      setPrompt({ title: t("cards.deleteDraftSuccess"), text: "", variant: "success" });
    } catch (err: unknown) {
      setConfirmDeleteDraftId(null);
      setPrompt({
        title: tCommon("error"),
        text: extractErrorMessage(err, "cards.deleteDraftError", t),
        variant: "error",
      });
    } finally {
      setDeletingDraftId(null);
    }
  }

  const toggleApplicationsExpansion = useCallback((contractId: number) => {
    setExpandedApplications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contractId)) {
        newSet.delete(contractId);
      } else {
        newSet.add(contractId);
      }
      return newSet;
    });
  }, []);

  return (
    <>
      {prompt && (
        <Prompt
          variant={prompt.variant || "success"}
          title={prompt.title}
          text={prompt.text}
          onClose={() => setPrompt(null)}
          namespace="ContractsPage"
        />
      )}
      {previewingContractId !== null && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.downloadingDraft.text")}
          additionalText={t("messages.loader.downloadingDraft.additionalText")}
          namespace="ContractsPage"
        />
      )}
      {previewingOrderCandidateId !== null && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.downloadingOrder.text")}
          additionalText={t("messages.loader.downloadingOrder.additionalText")}
          namespace="ContractsPage"
        />
      )}
      {documentSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${documentSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mt-6">
        {fillDrafts?.map(function renderDraftCard(draft) {
          const position = locale === "kk" ? draft.job_position_kk || draft.job_position_ru : draft.job_position_ru;
          return (
            <div key={`draft-${draft.id}`} className="flex flex-col gap-4 surface-component-fill radius-lg p-5">
              <div className="flex flex-col gap-2">
                <Badge
                  variant="soft"
                  color="gray"
                  text={t("status.draft")}
                  icon={<Edit2 size={14} color="currentColor" />}
                />
                <p className="text-body-bold-lg content-base-primary">{t("cards.fillDraftTitle")}</p>
              </div>

              <div className="p-2 radius-sm border surface-component-stroke flex items-center justify-between">
                <div className="flex justify-start items-center gap-2 min-w-0 flex-1">
                  <Avatar name={draft.candidate_name ?? "?"} textClassName="text-[14px] font-medium leading-3" />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <p className="text-label-sm content-base-primary truncate">{draft.candidate_name ?? "—"}</p>
                    {position && <span className="text-body-regular-xs content-base-secondary">{position}</span>}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-3 border-t surface-base-stroke">
                <div className="flex flex-col gap-1.5">
                  <p className="text-body-bold-xs content-base-secondary">{t("cards.managerSigned")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">{"—"}</p>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <p className="text-body-bold-xs content-base-secondary">{t("cards.employeeSigned")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">{"—"}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <p className="text-body-bold-xs content-base-secondary">{t("cards.createdAt")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">
                      {draft.updated_at ? formatDateForDisplay(draft.updated_at, true) : "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 border-t surface-base-stroke pt-3 mt-auto">
                <Button
                  variant="secondary"
                  size="md"
                  className="w-full"
                  onClick={() => navigate(`/hr/contracts/fill/${draft.application_id}`)}>
                  <Edit2 size={16} color="currentColor" />
                  {t("cards.continueProcessingTD")}
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  className="w-full"
                  disabled={deletingDraftId !== null}
                  onClick={() => setConfirmDeleteDraftId(draft.application_id)}>
                  <Trash size={16} color="currentColor" />
                  {t("cards.deleteDraftConfirm")}
                </Button>
              </div>
            </div>
          );
        })}
        {contracts.map(({ contract, resignationLetters, amendments }) => {
          const applicationItems = applicationItemsMap.get(contract.id) || [];

          const sortedApplicationItems = [...applicationItems].sort((a, b) => {
            function getTypePriority(type: ApplicationItem["type"]): number {
              if (type === "order" || type === "legacy_order") return 2;
              if (type === "job_application" || type === "legacy_job_application") return 3;
              return 1;
            }

            const priorityA = getTypePriority(a.type);
            const priorityB = getTypePriority(b.type);

            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }

            const dateA = "date" in a ? a.date : "";
            const dateB = "date" in b ? b.date : "";
            return new Date(dateB).getTime() - new Date(dateA).getTime();
          });

          const isExpanded = expandedApplications.has(contract.id);
          const shouldLimit = sortedApplicationItems.length > 3 && !isExpanded;
          const displayedApplications = shouldLimit ? sortedApplicationItems.slice(0, 3) : sortedApplicationItems;
          const hiddenCount = sortedApplicationItems.length - 3;

          return (
            <div key={contract.id} className="relative flex flex-col gap-4 surface-component-fill radius-lg p-5">
              <div className="flex flex-col gap-2">
                {contract.is_legacy_import ? (
                  <Badge variant="soft" color="positive" text={t("status.legacyImport")} icon={<CheckIcon />} />
                ) : (
                  <SigningStatus signingStatus={contract.signing_status} locale={locale} />
                )}
                <p className="text-body-bold-lg content-base-primary">
                  {t("cards.contract")} №{contract.contract_number}
                </p>
              </div>

              <div className="p-2 radius-sm border surface-component-stroke flex items-center justify-between">
                <div className="flex justify-start items-center gap-2 min-w-0 flex-1">
                  <Avatar name={contract.candidate_name ?? "?"} textClassName="text-[14px] font-medium leading-3" />

                  <div className="flex flex-col gap-0.5 min-w-0">
                    <p className="text-label-sm content-base-primary truncate">{contract.candidate_name ?? "—"}</p>
                    {locale === "kk"
                      ? contract.job_position_kk && (
                          <span className="text-body-regular-xs content-base-secondary">
                            {contract.job_position_kk}
                          </span>
                        )
                      : contract.job_position_ru && (
                          <span className="text-body-regular-xs content-base-secondary">
                            {contract.job_position_ru}
                          </span>
                        )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-3 border-t surface-base-stroke">
                <div className="flex flex-col gap-1.5">
                  <p className="text-body-bold-xs content-base-secondary">{t("cards.managerSigned")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">
                      {contract.signing_document?.org_signed_at
                        ? formatDateForDisplay(contract.signing_document.org_signed_at, true)
                        : "—"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <p className="text-body-bold-xs content-base-secondary">{t("cards.employeeSigned")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">
                      {contract.signing_document?.counter_signed_at
                        ? formatDateForDisplay(contract.signing_document.counter_signed_at, true)
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <p className="text-body-bold-xs content-base-secondary">{t("cards.createdAt")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">
                      {contract.created_at ? formatDateForDisplay(contract.created_at, true) : "—"}
                    </p>
                  </div>
                </div>
              </div>

              {applicationItems.length > 0 && (
                <div className="flex flex-col gap-1.5 border-t surface-base-stroke pt-3">
                  <span className="text-body-bold-xs content-base-secondary">{t("cards.letters")}</span>
                  <div className="flex flex-col gap-2">
                    {displayedApplications.map((item) => {
                      if (item.type === "job_application") {
                        const jobAppSignedUrl =
                          contract.job_application_signed_pdf_url || contract.job_application?.signed_pdf_url;
                        const jobAppOriginalUrl = contract.job_application_original_file_url;
                        const jobAppPdfUrl = jobAppSignedUrl || jobAppOriginalUrl;
                        const isJobAppFullySigned =
                          contract.job_application?.signing_status === SIGNING_STATUS_FULLY_SIGNED ||
                          !!jobAppSignedUrl;
                        const jobAppModalData = contract.candidate_application_id
                          ? {
                              type: "job_application" as const,
                              candidateId: contract.candidate_application_id,
                              candidateName: contract.candidate_name || "",
                              candidateStage: contract.candidate_stage,
                            }
                          : null;
                        return (
                          <div
                            key={`job_application-${item.contractId}`}
                            className="flex items-center gap-1.5 cursor-pointer hover:background-base-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isJobAppFullySigned && jobAppModalData) {
                                setSelectedModal(jobAppModalData);
                              } else if (jobAppPdfUrl) {
                                setPdfViewerData({
                                  fileUrl: jobAppPdfUrl,
                                  title: t("cards.jobApplicationPrefix"),
                                  secondaryFileUrl:
                                    jobAppSignedUrl && jobAppOriginalUrl ? jobAppOriginalUrl : undefined,
                                });
                              } else if (jobAppModalData) {
                                setSelectedModal(jobAppModalData);
                              }
                            }}>
                            <span className="content-action-brand">
                              <DocumentText1 size={16} color="currentColor" />
                            </span>
                            <span className="flex-1 text-body-regular-sm content-base-primary">
                              {t("cards.jobApplicationPrefix")}
                            </span>
                            <StatusIndicator
                              value={String(contract.job_application?.signing_status ?? 0)}
                              mapping={SIGNING_STATUS_INDICATOR_MAPPING}
                              defaultMapping={JOB_APPLICATION_DEFAULT}
                            />
                          </div>
                        );
                      }

                      if (item.type === "order") {
                        const orderSignedUrl = contract.order_signed_pdf_url || contract.order_on_hiring?.signed_pdf_url;
                        const orderOriginalUrl =
                          contract.order_original_file_url || contract.order_on_hiring?.original_file_url;
                        const orderPdfUrl = orderSignedUrl || orderOriginalUrl;
                        const isOrderFullySigned =
                          contract.order_on_hiring?.signing_status === SIGNING_STATUS_FULLY_SIGNED ||
                          !!orderSignedUrl;
                        const orderModalData = contract.order_on_hiring
                          ? {
                              type: "order" as const,
                              orderId: contract.order_on_hiring.id,
                              candidateName: contract.candidate_name || "",
                              originalFileUrl:
                                contract.order_on_hiring.original_file_url ?? contract.order_original_file_url ?? null,
                            }
                          : null;
                        return (
                          <div
                            key={`order-${item.contractId}`}
                            className="flex items-center gap-1.5 cursor-pointer hover:background-base-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isOrderFullySigned && orderModalData) {
                                setSelectedModal(orderModalData);
                              } else if (orderPdfUrl) {
                                setPdfViewerData({
                                  fileUrl: orderPdfUrl,
                                  title: t("order.fullPrefix"),
                                  secondaryFileUrl: orderSignedUrl && orderOriginalUrl ? orderOriginalUrl : undefined,
                                });
                              } else if (orderModalData) {
                                setSelectedModal(orderModalData);
                              }
                            }}>
                            <span className="content-action-brand">
                              <DocumentText1 size={16} color="currentColor" />
                            </span>
                            <span className="flex-1 text-body-regular-sm content-base-primary">
                              {t("order.fullPrefix")}
                            </span>
                            <StatusIndicator
                              value={String(contract.order_on_hiring?.signing_status ?? 0)}
                              mapping={SIGNING_STATUS_INDICATOR_MAPPING}
                              defaultMapping={JOB_APPLICATION_DEFAULT}
                            />
                          </div>
                        );
                      }

                      if (item.type === "legacy_job_application") {
                        return (
                          <div
                            key={`legacy-job-app-${item.contractId}`}
                            className="flex items-center gap-1.5 cursor-pointer hover:background-base-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.url) {
                                setPdfViewerData({
                                  fileUrl: item.url,
                                  title: t("cards.legacyJobApplicationPrefix"),
                                });
                              }
                            }}>
                            <span className="content-action-brand">
                              <DocumentText1 size={16} color="currentColor" />
                            </span>
                            <span className="flex-1 text-body-regular-sm content-base-primary">
                              {t("cards.legacyJobApplicationPrefix")}
                            </span>
                            <StatusIndicator value={undefined} mapping={{}} defaultMapping={LEGACY_UPLOADED_DEFAULT} />
                          </div>
                        );
                      }

                      if (item.type === "legacy_order") {
                        return (
                          <div
                            key={`legacy-order-${item.contractId}`}
                            className="flex items-center gap-1.5 cursor-pointer hover:background-base-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.url) {
                                setPdfViewerData({
                                  fileUrl: item.url,
                                  title: t("cards.legacyOrderPrefix"),
                                });
                              }
                            }}>
                            <span className="content-action-brand">
                              <DocumentText1 size={16} color="currentColor" />
                            </span>
                            <span className="flex-1 text-body-regular-sm content-base-primary">
                              {t("cards.legacyOrderPrefix")}
                            </span>
                            <StatusIndicator value={undefined} mapping={{}} defaultMapping={LEGACY_UPLOADED_DEFAULT} />
                          </div>
                        );
                      }

                      if (item.type === "resignation_letter") {
                        const resignation = resignationLetters.find((r) => r.id === item.resignationId);
                        if (!resignation) return null;
                        return (
                          <div
                            key={`resignation-${item.resignationId}`}
                            className="flex items-center gap-1.5 cursor-pointer hover:background-base-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedModal({
                                type: "resignation_letter",
                                resignationId: item.resignationId,
                              });
                            }}>
                            <span className="content-action-brand">
                              <DocumentText1 size={16} color="currentColor" />
                            </span>
                            <span className="flex-1 text-body-regular-sm content-base-primary">
                              {insertNumberAfterFirstWord(t("cards.resignationLetterPrefix"), resignation.id)}
                            </span>
                            <StatusIndicator value={resignation.status} mapping={RESIGNATION_STATUS_MAPPING} />
                          </div>
                        );
                      }

                      if (item.type === "amendment") {
                        const amendment = amendments.find((a) => a.id === item.amendmentId);
                        if (!amendment) return null;
                        return (
                          <div
                            key={`amendment-${item.amendmentId}`}
                            className="flex items-center gap-1.5 cursor-pointer hover:background-base-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedModal({
                                type: "amendment",
                                amendmentId: item.amendmentId,
                              });
                            }}>
                            <span className="content-action-brand">
                              <DocumentText1 size={16} color="currentColor" />
                            </span>
                            <span className="flex-1 text-body-regular-sm content-base-primary">
                              {insertNumberAfterFirstWord(
                                getAmendmentText(amendment.clause?.section_number, t),
                                amendment.amendment_number,
                              )}
                            </span>
                            <StatusIndicator
                              value={amendment.status}
                              mapping={AMENDMENT_STATUS_MAPPING}
                              defaultMapping={AMENDMENT_DEFAULT}
                            />
                          </div>
                        );
                      }

                      return null;
                    })}

                    {sortedApplicationItems.length > 3 && (
                      <button
                        type="button"
                        className="flex items-center justify-center gap-1.5 text-body-regular-sm content-action-brand hover:content-action-brand-hover cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleApplicationsExpansion(contract.id);
                        }}>
                        {isExpanded ? (
                          <>
                            {t("cards.hideApplications")}
                            <ArrowUp2 size={16} color="currentColor" />
                          </>
                        ) : (
                          <>
                            {t("cards.showMoreApplications", { count: hiddenCount })}
                            <ArrowDown2 size={16} color="currentColor" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <ContractActionsButtons
                className="mt-auto"
                contract={contract}
                isLoading={isLoading}
                downloadingContractId={downloadingContractId}
                previewingContractId={previewingContractId}
                submittingContractId={submittingContractId}
                previewingJobApplicationCandidateId={previewingJobApplicationCandidateId}
                reviewingJobApplicationCandidateId={reviewingJobApplicationCandidateId}
                previewingOrderCandidateId={previewingOrderCandidateId}
                completingHiringCandidateId={completingHiringCandidateId}
                onAction={handleAction}
              />
            </div>
          );
        })}
      </div>

      {selectedModal?.type === "resignation_letter" && (
        <ResignationLetterDetailsModal
          resignationId={selectedModal.resignationId}
          onClose={() => setSelectedModal(null)}
        />
      )}

      {pdfViewerData && (
        <Suspense fallback={null}>
          <PdfViewer
            fileUrl={pdfViewerData.fileUrl}
            title={pdfViewerData.title}
            secondaryFileUrl={pdfViewerData.secondaryFileUrl}
            onClose={() => setPdfViewerData(null)}
          />
        </Suspense>
      )}

      {selectedModal?.type === "amendment" && (
        <AmendmentDetailsModal amendmentId={selectedModal.amendmentId} onClose={() => setSelectedModal(null)} />
      )}

      {selectedModal?.type === "job_application" && (
        <JobApplicationDetailsModal
          candidateName={selectedModal.candidateName}
          candidateId={selectedModal.candidateId}
          candidateStage={selectedModal.candidateStage}
          onClose={() => setSelectedModal(null)}
          locale={locale}
        />
      )}

      {selectedModal?.type === "order" && (
        <OrderDetailsModal
          orderId={selectedModal.orderId}
          candidateName={selectedModal.candidateName}
          originalFileUrlOverride={selectedModal.originalFileUrl}
          onClose={() => setSelectedModal(null)}
          locale={locale}
        />
      )}

      {confirmDeleteDraftId !== null && (
        <PromptForm
          variant="error"
          title={t("cards.deleteDraftConfirmTitle")}
          text={t("cards.deleteDraftConfirmText")}
          confirmText={t("cards.deleteDraftConfirm")}
          cancelText={t("cards.deleteDraftCancel")}
          onConfirm={() => handleDeleteDraft(confirmDeleteDraftId)}
          onClose={() => setConfirmDeleteDraftId(null)}
          isLoading={deletingDraftId !== null}
          namespace="ContractsPage"
        />
      )}
    </>
  );
}
